import { useEffect, useRef, useState } from "react";
import { MdBusiness, MdDelete, MdEdit, MdLocationOn, MdSync } from "react-icons/md";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, FormModal, Input, SelectField, Textarea } from "../../components/ui";
import HighlightText from "../../shared/HighlightText";
import { formatDateFr } from "../../shared/dateFormat";
import { habColor } from "../../shared/habilitations";
import { criticiteColor } from "../../shared/statusColors";
import { COLORS, RADIUS, SPACING } from "../../shared/theme";

const MESURES_SECURITE = ["Chiffrement", "Contrôle d'accès", "Pseudonymisation", "Journalisation", "Sauvegardes", "Pare-feu", "Antivirus", "Formation utilisateurs", "Procédures internes"];
const TYPE_ELEMENT = ["Serveur physique", "Serveur virtuel", "Base de données", "Application métier", "SaaS/Cloud", "Firewall/Router", "NAS/Stockage", "Poste de travail", "Autre"];
const LOCALISATION = ["On-premise (Datacenter interne)", "Cloud public (AWS/Azure/GCP)", "Hébergeur externe", "Hybride", "Autre"];
const CRITICITE = ["Critique", "Importante", "Normale", "Faible"];

const emptyElementSI = {
  nom: "", type: "Serveur virtuel", localisation: "Cloud public (AWS/Azure/GCP)", localisationDetail: "",
  criticite: "Normale", donneesHebergees: "", responsableTechnique: "", sousTraitantConcerne: "",
  mesuresSecurite: [], derniereMAJ: "", observations: "",
};

export default function PageInventaireSI({ elementsSI, setElementsSI, personnes, sousTraitants, searchHighlight }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const highlightRef = useRef(null);
  const nextId = useRef(100);
  const [form, setForm] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleArray = (k, val) => setForm(f => ({ ...f, [k]: f[k].includes(val) ? f[k].filter(v => v !== val) : [...f[k], val] }));

  const openNew = () => { setEditing(null); setForm({ ...emptyElementSI, mesuresSecurite: [] }); setShowForm(true); };
  const openEdit = el => { setEditing(el); setForm({ ...el }); setShowForm(true); };
  const save = () => {
    if (editing) setElementsSI(els => els.map(e => e.id === editing.id ? { ...form, id: editing.id } : e));
    else setElementsSI(els => [...els, { ...form, id: nextId.current++ }]);
    setShowForm(false); setEditing(null); setForm(null);
  };

  const pOptions = [{ value: "", label: "— Sélectionner —" }, ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom}` }))];
  const stOptions = [{ value: "", label: "— Aucun —" }, ...sousTraitants.map(s => ({ value: s.nom, label: s.nom }))];
  const filtered = filter === "Tous" ? elementsSI : elementsSI.filter(e => e.type === filter);

  const highlightId = searchHighlight?.id != null ? Number(searchHighlight.id) : null;
  const searchTerm = searchHighlight?.searchTerm || null;

  useEffect(() => {
    if (highlightId != null) {
      const el = elementsSI.find(e => e.id === highlightId || Number(e.id) === highlightId);
      if (el?.type) setFilter(el.type);
    }
  }, [highlightId, elementsSI]);

  useEffect(() => {
    if (highlightId != null && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId]);

  const stats = {
    total: elementsSI.length,
    critiques: elementsSI.filter(e => e.criticite === "Critique").length,
    cloud: elementsSI.filter(e => e.localisation.includes("Cloud")).length,
  };

  return (
    <PageShell>
      <PageHeader
        title="Inventaire SI"
        subtitle="Serveurs, bases de données, applications, infrastructure cloud"
        actions={
          <button type="button" className="page-shell__btn page-shell__btn--primary" onClick={openNew}>
            + Nouvel élément SI
          </button>
        }
      />

      <PageContent>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 22 }}>
          {[
            { label: "Total éléments", val: stats.total, color: "#1e40af", bg: "#eff6ff", border: "#bfdbfe" },
            { label: "Critiques", val: stats.critiques, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
            { label: "Cloud", val: stats.cloud, color: "#0c4a6e", bg: "#f0f9ff", border: "#7dd3fc" },
          ].map(k => (
            <div key={k.label} style={{ background: k.bg, borderRadius: RADIUS.lg, padding: "14px 18px", border: `1.5px solid ${k.border}` }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.val}</div>
              <div style={{ fontSize: 12, color: k.color, marginTop: 3, fontWeight: 600 }}>{k.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 600 }}>Type :</span>
          <button onClick={() => setFilter("Tous")} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === "Tous" ? "#1e40af" : COLORS.white, color: filter === "Tous" ? COLORS.white : "#374151", border: `1.5px solid ${filter === "Tous" ? "#1e40af" : COLORS.border}` }}>Tous</button>
          {TYPE_ELEMENT.slice(0, 6).map(t => (
            <button key={t} onClick={() => setFilter(t)} style={{ padding: "4px 12px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: filter === t ? "#3b82f6" : COLORS.white, color: filter === t ? COLORS.white : "#374151", border: `1.5px solid ${filter === t ? "#3b82f6" : COLORS.border}` }}>{t}</button>
          ))}
        </div>

        {showForm && form && (
          <FormModal
            title={editing ? "Modifier l'élément SI" : "Nouvel élément SI"}
            headerBackground="#1e40af"
            maxWidth={720}
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" onClick={() => { setShowForm(false); setForm(null); setEditing(null); }} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Annuler</button>
                <button type="button" onClick={save} style={{ padding: "9px 18px", borderRadius: RADIUS.md, border: "none", background: "#0ea5e9", color: COLORS.white, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Enregistrer</button>
              </div>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "span 2" }}><FieldGroup label="Nom de l'élément"><Input value={form.nom} onChange={set("nom")} placeholder="Ex : Serveur Web Production" /></FieldGroup></div>
              <FieldGroup label="Type"><SelectField value={form.type} onChange={set("type")} options={TYPE_ELEMENT} /></FieldGroup>
              <FieldGroup label="Criticité"><SelectField value={form.criticite} onChange={set("criticite")} options={CRITICITE} /></FieldGroup>
              <FieldGroup label="Localisation"><SelectField value={form.localisation} onChange={set("localisation")} options={LOCALISATION} /></FieldGroup>
              <FieldGroup label="Détail localisation"><Input value={form.localisationDetail} onChange={set("localisationDetail")} placeholder="Ex : AWS Paris eu-west-3" /></FieldGroup>
              <div style={{ gridColumn: "span 2" }}><FieldGroup label="Données hébergées"><Textarea value={form.donneesHebergees} onChange={set("donneesHebergees")} rows={2} placeholder="Quelles données sont stockées/traitées sur cet élément ?" /></FieldGroup></div>
              <FieldGroup label="Responsable technique"><SelectField value={form.responsableTechnique} onChange={set("responsableTechnique")} options={pOptions} /></FieldGroup>
              <FieldGroup label="Sous-traitant/Hébergeur"><SelectField value={form.sousTraitantConcerne} onChange={set("sousTraitantConcerne")} options={stOptions} /></FieldGroup>
              <div style={{ gridColumn: "span 2" }}>
                <FieldGroup label="Mesures de sécurité">
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                    {MESURES_SECURITE.map(m => {
                      const selected = form.mesuresSecurite.includes(m);
                      return <button key={m} onClick={() => toggleArray("mesuresSecurite", m)} type="button" style={{ padding: "4px 10px", borderRadius: RADIUS.pill, fontSize: 11, fontWeight: 600, cursor: "pointer", background: selected ? "#eff6ff" : "#f9fafb", color: selected ? "#1d4ed8" : COLORS.textSecondary, border: `1.5px solid ${selected ? "#bfdbfe" : COLORS.border}` }}>{m}</button>;
                    })}
                  </div>
                </FieldGroup>
              </div>
              <FieldGroup label="Dernière MAJ"><Input type="date" value={form.derniereMAJ} onChange={set("derniereMAJ")} /></FieldGroup>
              <div />
              <div style={{ gridColumn: "span 2" }}><FieldGroup label="Observations (RTO/RPO, procédures...)"><Textarea value={form.observations} onChange={set("observations")} rows={2} /></FieldGroup></div>
            </div>
          </FormModal>
        )}

        {filtered.map(el => {
          const cc = criticiteColor[el.criticite] || criticiteColor["Normale"];
          const resp = personnes.find(p => p.id === el.responsableTechnique || p.id === Number(el.responsableTechnique));
          const hc = resp ? (habColor[resp.habilitation] || habColor["Autre"]) : null;
          const isHighlighted = el.id === highlightId || Number(el.id) === highlightId;
          return (
            <div
              key={el.id}
              ref={isHighlighted ? highlightRef : null}
              style={{
                background: isHighlighted ? "#fffbeb" : COLORS.white,
                borderRadius: RADIUS.lg,
                border: `1.5px solid ${isHighlighted ? "#f59e0b" : COLORS.border}`,
                padding: 18,
                marginBottom: 12,
                boxShadow: isHighlighted ? "0 0 0 2px #fef3c7" : "0 1px 4px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.textPrimary, marginBottom: 5 }}><HighlightText text={el.nom} term={searchTerm} /></div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontWeight: 600 }}>{el.type}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: cc.bg, color: cc.text, border: `1px solid ${cc.border}`, fontWeight: 700 }}>{el.criticite}</span>
                    <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: RADIUS.pill, background: "#f0fdf4", color: "#15803d", border: "1px solid #86efac", fontWeight: 600 }}>{el.localisation}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => openEdit(el)} style={{ padding: `${SPACING.xs}px 9px`, borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "transparent", color: COLORS.primaryAccent, fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdEdit size={14} /></button>
                  <button onClick={() => setElementsSI(els => els.filter(e => e.id !== el.id))} style={{ padding: "4px 9px", borderRadius: RADIUS.sm, border: "1.5px solid #fee2e2", background: "#fff5f5", color: "#dc2626", fontSize: 12, cursor: "pointer", display: "inline-flex", alignItems: "center" }}><MdDelete size={14} /></button>
                </div>
              </div>
              {el.donneesHebergees && <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}><strong>Données :</strong> <HighlightText text={el.donneesHebergees} term={searchTerm} /></div>}
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, alignItems: "center" }}>
                {resp && hc && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: hc.bg, border: `1.5px solid ${hc.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: hc.text }}>{resp.prenom[0]}{resp.nom[0]}</div><span>{resp.prenom} {resp.nom}</span></div>}
                {el.localisationDetail && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdLocationOn size={13} /> {el.localisationDetail}</span>}
                {el.sousTraitantConcerne && <span style={{ color: "#7c3aed", display: "inline-flex", alignItems: "center", gap: 4 }}><MdBusiness size={13} /> {el.sousTraitantConcerne}</span>}
                {el.derniereMAJ && <span style={{ color: COLORS.textSecondary, display: "inline-flex", alignItems: "center", gap: 4 }}><MdSync size={13} /> {formatDateFr(el.derniereMAJ)}</span>}
              </div>
            </div>
          );
        })}
      </PageContent>
    </PageShell>
  );
}
